import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BaseQueryOpts } from '../baseTypes';

import * as batshit from '@yornaath/batshit';
import { getMessagingToDosWithStatus, getMessagingToDos, archiveMessagingToDo, completeMessagingToDo, createMessagingToDo, MessagingToDoStatus, unarchiveMessagingToDo, uncompleteMessagingToDo, getConversationToDoIds } from '@uplift/api';


export const messagingToDosBatch = batshit.create({
  fetcher: async (todoIds: string[]) => {
    const response = await getMessagingToDos(todoIds.filter(Boolean));

    return response.data;
  },
  resolver: (response, key) => response.find(t => t.id === key)!,
  scheduler: batshit.windowScheduler(10)
});


export const useMessagingToDosWithStatusQuery = (status: MessagingToDoStatus = 'active', opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'messaging', 'todo', status ],
    queryFn: async () => {
      return await getMessagingToDosWithStatus(status);
    },
    staleTime: Infinity,
    select: res => res.data,
    ...opts,
  });
};
export const useMessagingToDoQuery = (todoId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'messaging', 'todo', todoId ],
    queryFn: async () => {
      if (!todoId) {
        return null;
      }

      return await messagingToDosBatch.fetch(todoId);
    },
    staleTime: Infinity,
    ...opts,
  });
};

export const useAddMessagingToDoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: { messageId: string; description: string }) => createMessagingToDo(args.messageId, args.description),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [ 'messaging', 'todo' ] });
    },
  });
};

export const useArchiveMessagingToDoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {todoId: string; archive: boolean}) => {
      if (args.archive) {
        return archiveMessagingToDo(args.todoId);
      }
      return unarchiveMessagingToDo(args.todoId);
    },
    async onSuccess(data, variables) {
      await queryClient.invalidateQueries({ queryKey: [ 'messaging', 'todo' ] });
      await queryClient.invalidateQueries({ queryKey: [ 'messaging', 'todo', variables ] });
    },
  });
};

export const useCompleteMessagingToDoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {todoId: string; complete: boolean}) => {
      if (args.complete) {
        return completeMessagingToDo(args.todoId);
      }
      return uncompleteMessagingToDo(args.todoId);
    },
    async onSuccess(data, variables) {
      await queryClient.invalidateQueries({ queryKey: [ 'messaging', 'todo' ] });
      await queryClient.invalidateQueries({ queryKey: [ 'messaging', 'todo', variables ] });
    },
  });
};


export const useConversationToDosQuery = (conversationId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'messaging', 'todo', conversationId ],
    keepPreviousData: true,
    queryFn: async () => {
      const todoIdsResponse = await getConversationToDoIds(conversationId);
      const todoIds = todoIdsResponse.data;
      const todos = await Promise.all(todoIds.map(toDoId => messagingToDosBatch.fetch(toDoId)));

      return todos;
    },
    ...opts,
  });
};
